'use client';

import { useContext, useState, useEffect } from 'react';
import { useBuildDownloadUrl } from '../../../hooks/use-download-url-builder';
import { useTranslation } from '../../../hooks/use-translation';
import SpriteIcon from '../../shared/sprite-icon';
import { MenuItem } from './menu-item';
import GlobalContext from '../../../context/global-context';
import { OWLogo } from './logo';
import { useClassName } from '../../../hooks/use-class-name';
import { HeaderSearch } from './search/search-header';
import OWLink from '../../shared/ow-link';
import { usePathname } from 'next/navigation';
import { Login } from '../../login/login';
import { useUserProfile } from '../../../context/user-profile-context';
import {
  useDownloadClickHandler,
  DownloadClickComponent,
} from '../../../hooks/use-download-click-handler';
import { HeaderLoginButton } from './header-login-button';
import useIsOverwolfIframe from '../../../hooks/use-is-overwolf-iframe';
import { useResizeTransitionGuard } from '../../../hooks/use-resize-transition-guard';

// -----------------------------------------------------------------------------
export const PageHeader = (): JSX.Element => {
  // Suppress CSS transitions while the window is resized so the nav sub-menu
  // doesn't flash when the viewport crosses a media-query breakpoint.
  useResizeTransitionGuard();

  const downloadUrl = useBuildDownloadUrl({
    utmContent: 'new-light',
    utmSource: 'web_app_store',
  });

  const { isLoggedIn, waitForLoginCheck } = useUserProfile();
  const [isCheckingLogin, setIsCheckingLogin] = useState(true);

  const checkLogin = async () => {
    await waitForLoginCheck();
    setIsCheckingLogin(false);
  };

  useEffect(() => {
    void checkLogin();
  }, [waitForLoginCheck]);

  const { config } = useContext(GlobalContext);
  const { appstore } = config?.publicRuntimeConfig?.links;
  const { baseUrl } = config?.publicRuntimeConfig?.base;
  const pathname = usePathname();
  const isAppPage = pathname?.startsWith('/app/');
  // Inside the Overwolf app (store embedded in an iframe) the client is already
  // installed, so the "Download Overwolf" CTA is irrelevant — hide it. Default
  // the CTA off and only reveal it once the post-mount check has resolved and
  // confirmed we're neither on an app page nor in the iframe, so it never
  // flashes in before detection completes.
  const { isOverwolfIframe, isResolved } = useIsOverwolfIframe();
  const showDownloadOverwolf = isResolved && !isAppPage && !isOverwolfIframe;

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { handleClickDownload } = useDownloadClickHandler({
    item: {
      id: 'overwolf-client',
      title: 'Overwolf',
      isElectron: false,
      alternativeDownloads: [
        {
          label: 'Overwolf',
          platform: 'Windows',
          url: downloadUrl,
        },
      ],
    },
    component: DownloadClickComponent.PageHeader,
  });

  const { t } = useTranslation();

  const hamburgerMenuClassNames = useClassName(
    {
      'is-open': isOpen,
    },
    'hamburger-menu-button',
  );

  const mobileMenuHeaderClassNames = useClassName(
    {
      'is-open': isOpen,
    },
    'header-inner',
  );

  const handleMobileMenuOpenClose = (): void => {
    setIsOpen((isOpen) => !isOpen);
  };

  const handleDownloadButtonClick = async (event: any): Promise<void> => {
    await handleClickDownload(event, downloadUrl);
  };

  // const handleClickDownload = (event: React.MouseEvent<HTMLElement>): void => {
  //   const url = Utils.InstallerDownloadOverwrite(event);
  //   analytics.track('appstore_download_clicked', {
  //     appId: 'Clean client',
  //     appName: 'Clean client',
  //     component: DownloadClickComponent.OneApp,
  //     platform: 'Web',
  //   });

  //   analytics.gtagPush({
  //     event: 'app_download_cta',
  //     app_name: 'Clean client',
  //     app_id: 'Clean client',
  //     app_os: OS,
  //   });

  //   dispatch({
  //     type: AppBusEventType.App.ShowDownloadModal,
  //     data: { isOpen: true, currentApp: null, url },
  //   });
  // };

  return (
    <>
      <header>
        <div className={mobileMenuHeaderClassNames}>
          <div className="left-side">
            <button
              id="hamburger-menu-button"
              className={hamburgerMenuClassNames}
              onClick={handleMobileMenuOpenClose}
            >
              <div>
                <span />
                <span />
                <span />
              </div>
            </button>

            <OWLink href={`${appstore}/`} className="logo-area">
              <span className="sr-only">Overwolf Appstore</span>
              <OWLogo />
            </OWLink>

            <nav className="navbar">
              <ul className="menu-list">
                <MenuItem
                  name={t('browseApps')}
                  type="Button"
                  hasSubMenu={true}
                />
              </ul>
            </nav>
          </div>
          <HeaderSearch />

          <div className="right-side">
            <OWLink
              href={`${baseUrl}/creators/build-an-app/`}
              className="btn-secondary small icon cta-btn"
              target="_blank"
            >
              {t('footer.buildApp')}
              <SpriteIcon icon="arrowRight" />
            </OWLink>

            {showDownloadOverwolf && (
              <OWLink
                onClick={async (event: any) => {
                  await handleDownloadButtonClick(event);
                }}
                href={downloadUrl}
                className="btn-primary small dl-btn"
                download="OverwolfInstaller"
              >
                {t('downloadOverwolf')}
              </OWLink>
            )}

            <div className="separator" />
            <HeaderLoginButton
              isCheckingLogin={isCheckingLogin}
              isLoggedIn={isLoggedIn}
            />

            {/* {isCheckingLogin && (
              <div className="rotating-loader">
                <SpriteIcon icon="loader" />
              </div>
            )}

            {!isCheckingLogin && (
              <>
                {!isLoggedIn && (
                  <button
                    onClick={() => {
                      dispatch({
                        type: AppBusEventType.App.ShowLoginModal,
                        data: { show: true, source: 'header' },
                      });
                    }}
                    className="clean-btn login-btn"
                  >
                    Login
                  </button>
                )}

                {isLoggedIn && <UserSettingsMenu />}
              </>
            )} */}
          </div>
        </div>
      </header>
      <Login />
    </>
  );
};
